import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Users, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import Layout from '../components/layout/Layout';
import { siteConfig, heroQuote, missionSection, services, ctaSection } from '../data/mock';

const Home = () => {
  const getIcon = (iconName) => {
    switch (iconName) {
      case 'BookOpen':
        return <BookOpen className="w-6 h-6" />;
      case 'Users':
        return <Users className="w-6 h-6" />;
      default:
        return <BookOpen className="w-6 h-6" />;
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#f5f5f0]/80 via-[#f5f5f0]/70 to-[#f5f5f0]/90" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          {/* Badge */}
          <div className="inline-block mb-8">
            <span className="bg-white/80 backdrop-blur-sm text-gray-700 px-4 py-2 rounded-full text-sm font-medium shadow-sm">
              Dementia Therapy & Death Doula Services
            </span>
          </div>

          {/* Main Heading */}
          <h1 className="text-5xl md:text-7xl font-serif text-gray-800 mb-6 leading-tight">
            {siteConfig.name}
          </h1>

          {/* Tagline */}
          <p className="text-2xl md:text-3xl text-gray-600 italic mb-8 font-serif">
            {siteConfig.tagline}
          </p>

          {/* Description */}
          <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
            {siteConfig.description}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/services" className="w-full sm:w-auto flex justify-center">
              <Button className="bg-[#8B9D77] hover:bg-[#7A8B68] text-white px-8 py-6 rounded-full text-base font-medium transition-all duration-300 hover:shadow-lg flex items-center gap-2">
                Our Services
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/contact" className="w-full sm:w-auto flex justify-center">
              <Button variant="outline" className="border-2 border-gray-400 text-gray-700 hover:bg-gray-100 px-8 py-6 rounded-full text-base font-medium transition-all duration-300">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-xl md:text-2xl italic text-gray-700 font-serif mb-4">
            "{heroQuote.text}"
          </p>
          <p className="text-[#A88B4A] font-medium">{heroQuote.reference}</p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-[#f9f9f6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              <span className="text-[#A88B4A] font-medium text-sm tracking-widest uppercase mb-4 block">
                Our Mission
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-gray-800 mb-6 leading-tight">
                {missionSection.title}
              </h2>
              <p className="text-gray-600 leading-relaxed text-lg">
                {missionSection.content}
              </p>
            </div>

            {/* Right Quote Card */}
            <div className="relative">
              <div className="bg-gradient-to-br from-[#d4dcc4] to-[#c9b896] rounded-2xl p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-6">
                  <Heart className="w-full h-full text-[#8B9D77] stroke-1" />
                </div>
                <p className="text-xl md:text-2xl italic text-gray-800 font-serif mb-4">
                  "{missionSection.quote.text}"
                </p>
                <p className="text-[#A88B4A] font-medium">{missionSection.quote.reference}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="text-[#A88B4A] font-medium text-sm tracking-widest uppercase mb-4 block">
              How We Serve
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-gray-800">
              Compassionate Care Services
            </h2>
          </div>

          {/* Service Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {services.map((service) => (
              <Card 
                key={service.id} 
                className="bg-[#f5f5f0] border-0 p-8 rounded-2xl hover:shadow-lg transition-all duration-300 group"
              >
                <div className="w-14 h-14 rounded-full bg-[#d4dcc4] flex items-center justify-center mb-6 text-[#6B7A5D]">
                  {getIcon(service.icon)}
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>
                <Link 
                  to="/services"
                  className="inline-flex items-center gap-2 text-[#8B9D77] font-medium hover:gap-3 transition-all duration-300"
                >
                  Learn more
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-16 bg-[#f9f9f6]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <span className="text-[#A88B4A] font-medium text-sm tracking-widest uppercase mb-4 block">
            Our Vision
          </span>
          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed font-serif italic">
            We envision a world where individuals living with dementia are seen, known, and valued for who they are — not defined by memory loss but honored for their life stories.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-gray-800 mb-6">
            {ctaSection.title}
          </h2>
          <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
            {ctaSection.description}
          </p>
          <Link to="/contact">
            <Button className="bg-[#A88B4A] hover:bg-[#967A3E] text-white px-10 py-6 rounded-full text-base font-medium transition-all duration-300 hover:shadow-lg flex items-center gap-2 mx-auto">
              {ctaSection.buttonText}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
